import { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Card,
  CardContent,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Iconify from '../../components/iconify';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../layouts/loading';
import { CompOffRequestForm, CompOffTable, CompOffFilters } from './components';
import { COMP_OFF_STATUS } from './constants';
import usePersistedState from '../../hooks/usePersistedState';
import {
  createCompOffRequest,
  getMyCompOffRequests,
  getAllCompOffRequests,
  cancelCompOffRequest,
  updateCompOffRequest,
  bulkUpdateCompOffStatus,
  getCompOffBalance,
} from '../../services/compOffService';
import { isAdmin, isManager, isHR, checkComponentPermission } from '../../utils/share';

const queryClient = new QueryClient();

function CompOffContent() {
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingCompOff, setEditingCompOff] = useState(null);
  const isAdminUser = isAdmin();
  const isManagerUser = isManager();
  const isHRUser = isHR();
  // Persisted so returning from a request's details page lands back on the same tab, filters and sort
  const [activeTab, setActiveTab] = usePersistedState(
    'compOff.activeTab',
    (isManagerUser || isHRUser) && !isAdminUser ? 1 : 0
  );
  const [filters, setFilters] = usePersistedState('compOff.filters', {});
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
  // { sortBy, sortOrder } — empty means the backend default (pending first, newest first)
  const [sort, setSort] = usePersistedState('compOff.sort', {});
  const [selectedIds, setSelectedIds] = useState([]);
  // Bulk is approve-only: a rejection needs its own reason, so it stays per request
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingId, setCancelingId] = useState(null);
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const isAdminOrManagerOrHR = isAdminUser || isManagerUser || isHRUser;

  const queryClientInstance = useQueryClient();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === undefined || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      setPagination((prev) => ({ ...prev, page: 1 }));
      return newFilters;
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ page: 1, pageSize: newPageSize });
  };

  const handleSort = (columnId) => {
    setSort((prev) => {
      if (prev.sortBy !== columnId || !prev.sortOrder) {
        return { sortBy: columnId, sortOrder: 'asc' };
      }
      if (prev.sortOrder === 'asc') {
        return { sortBy: columnId, sortOrder: 'desc' };
      }
      return {};
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Selection only spans the rows currently on screen
  useEffect(() => {
    setSelectedIds([]);
  }, [filters, pagination, sort, activeTab]);

  const { data: balanceData } = useQuery({
    queryKey: ['compOffBalance'],
    queryFn: async () => {
      try {
        const response = await getCompOffBalance();
        return response?.data?.data || { totalCredits: 0 };
      } catch (error) {
        return { totalCredits: 0 };
      }
    },
    enabled: activeTab === 0,
  });

  const totalCredits = balanceData?.totalCredits ?? 0;

  const { data: myData = { rows: [], count: 0 }, isLoading: isLoadingMy } = useQuery({
    queryKey: ['myCompOffRequests', filters, pagination, sort],
    queryFn: async () => {
      try {
        const response = await getMyCompOffRequests({ ...filters, ...sort }, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching your comp-off requests');
        return { rows: [], count: 0 };
      }
    },
  });

  const myRequests = myData.rows || [];
  const myCount = myData.count || 0;

  const { data: allData = { rows: [], count: 0 }, isLoading: isLoadingAll } = useQuery({
    queryKey: ['allCompOffRequests', filters, pagination, sort],
    queryFn: async () => {
      try {
        const response = await getAllCompOffRequests({ ...filters, ...sort }, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching all comp-off requests');
        return { rows: [], count: 0 };
      }
    },
    enabled: isAdminOrManagerOrHR,
  });

  const allRequests = allData.rows || [];
  const allCount = allData.count || 0;

  const invalidateQueries = () => {
    queryClientInstance.invalidateQueries(['myCompOffRequests']);
    queryClientInstance.invalidateQueries(['compOffBalance']);
    if (isAdminOrManagerOrHR) {
      queryClientInstance.invalidateQueries(['allCompOffRequests']);
    }
  };

  const createMutation = useMutation({
    mutationFn: createCompOffRequest,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Comp-off request submitted successfully');
      setOpenFormModal(false);
      setEditingCompOff(null);
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error submitting comp-off request');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateCompOffRequest(id, data),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Comp-off request updated successfully');
      setOpenFormModal(false);
      setEditingCompOff(null);
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error updating comp-off request');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: cancelCompOffRequest,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Comp-off request deleted successfully');
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error deleting comp-off request');
    },
  });

  // Mutation for approving the selected comp-off requests in one go
  const bulkStatusMutation = useMutation({
    mutationFn: bulkUpdateCompOffStatus,
    onSuccess: (response) => {
      const { succeeded = [], failed = [] } = response?.data?.data || {};
      if (succeeded.length > 0) {
        toast.success(response?.data?.message || 'Comp-off requests approved successfully');
      }
      if (failed.length > 0) {
        toast.error(`${failed.length} request(s) could not be approved: ${failed[0].message}`);
      }
      setSelectedIds([]);
      setBulkApproveOpen(false);
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error approving comp-off requests');
    },
  });

  const handleConfirmBulkApprove = () => {
    bulkStatusMutation.mutate({ ids: selectedIds, status: COMP_OFF_STATUS.APPROVED });
  };

  const handleCreateClick = () => {
    setEditingCompOff(null);
    setOpenFormModal(true);
  };

  const handleEditCompOff = useCallback(
    (compOffId) => {
      const toEdit =
        activeTab === 0 ? myRequests.find((r) => r.id === compOffId) : allRequests.find((r) => r.id === compOffId);
      if (toEdit) {
        setEditingCompOff(toEdit);
        setOpenFormModal(true);
      }
    },
    [activeTab, myRequests, allRequests]
  );

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
    setEditingCompOff(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (values) => {
      if (editingCompOff) {
        await updateMutation.mutateAsync({ id: editingCompOff.id, data: values });
      } else {
        await createMutation.mutateAsync(values);
      }
      return true;
    },
    [createMutation, updateMutation, editingCompOff]
  );

  const handleCancelCompOff = useCallback((id) => {
    setCancelingId(id);
    setCancelDialogOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (cancelingId) {
      cancelMutation.mutate(cancelingId);
      setCancelDialogOpen(false);
      setCancelingId(null);
    }
  }, [cancelingId, cancelMutation]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const effectiveTab = activeTab;
  const isLoading = effectiveTab === 0 ? isLoadingMy : isLoadingAll;
  const currentRequests = effectiveTab === 0 ? myRequests : allRequests;
  const currentCount = effectiveTab === 0 ? myCount : allCount;

  // Same rule as the details page: admins can review any pending request, managers all but their own
  const canBulkReview =
    effectiveTab === 1 && (isAdminUser || isManagerUser) && checkComponentPermission('APPROVE_COMP_OFF');
  const bulkSelectableIds = canBulkReview
    ? allRequests
        .filter((compOff) => compOff.status === COMP_OFF_STATUS.PENDING && (isAdminUser || compOff.userId !== userId))
        .map((compOff) => compOff.id)
    : [];

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Comp-Off', path: '' },
  ];

  return (
    <>
      <Helmet>
        <title> Comp-Off </title>
      </Helmet>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            {effectiveTab === 0 && checkComponentPermission('CREATE_COMP_OFF') && (
              <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleCreateClick}>
                Request Comp-Off
              </Button>
            )}
          </Stack>
        </Stack>

        {effectiveTab === 0 && (
          <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Total Comp-Off Credits
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {totalCredits} {totalCredits === 1 ? 'day' : 'days'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: 'block' }}>
                    Approved credits considered by HR during monthly salary processing
                  </Typography>
                </Box>
                <Tooltip title="Total approved compensatory off days earned by working on weekends or holidays">
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                    }}
                  >
                    <CalendarToday sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                </Tooltip>
              </Stack>
            </CardContent>
          </Card>
        )}

        {isAdminOrManagerOrHR ? (
          <Card sx={{ mb: 3 }}>
            <Tabs value={effectiveTab} onChange={handleTabChange}>
              <Tab label="My Comp-Off" />
              <Tab label="All Comp-Off" />
            </Tabs>
          </Card>
        ) : (
          <Card sx={{ mb: 3 }}>
            <Tabs value={0} sx={{ pointerEvents: 'none' }}>
              <Tab label="My Comp-Off" />
            </Tabs>
          </Card>
        )}

        <CompOffFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          showEmployeeFilter={effectiveTab === 1 && isAdminOrManagerOrHR}
        />

        <Loading open={isLoading} />
        <Card>
          <Box sx={{ p: 3 }}>
            <CompOffTable
              compOffRequests={currentRequests}
              onCancelCompOff={handleCancelCompOff}
              onEditCompOff={handleEditCompOff}
              showEmployee={effectiveTab === 1}
              currentUserId={userId}
              count={currentCount}
              page={pagination.page - 1}
              rowsPerPage={pagination.pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              sort={sort}
              onSort={handleSort}
              selectedIds={selectedIds}
              selectableIds={bulkSelectableIds}
              onSelectionChange={canBulkReview ? setSelectedIds : undefined}
              selectionActions={
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<Iconify icon="eva:checkmark-fill" />}
                  onClick={() => setBulkApproveOpen(true)}
                >
                  Approve selected
                </Button>
              }
            />
          </Box>
        </Card>

        {openFormModal && (
          <CompOffRequestForm
            open
            onClose={handleFormModalClose}
            onSubmit={handleFormSubmit}
            compOffData={editingCompOff}
            isEdit={!!editingCompOff}
          />
        )}

        {/* Bulk approve confirmation */}
        <Dialog open={bulkApproveOpen} onClose={() => setBulkApproveOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Approve {selectedIds.length} comp-off request{selectedIds.length === 1 ? '' : 's'}?
          </DialogTitle>
          <DialogContent>
            <Typography>
              Credit days are calculated from the hours worked. Each employee will be notified by email.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkApproveOpen(false)}>Cancel</Button>
            <LoadingButton
              variant="contained"
              color="success"
              onClick={handleConfirmBulkApprove}
              loading={bulkStatusMutation.isLoading}
            >
              Approve
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>Delete Comp-Off Request</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this comp-off request? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleConfirmCancel}
              loading={cancelMutation.isLoading}
            >
              Yes, Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}

export default function CompOffPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <CompOffContent />
    </QueryClientProvider>
  );
}
